<template>
  <q-toolbar
    id="navbar"
    class="bg-white text-primary"
    :class="{ 'q-pl-none': $q.screen.lt.md }"
    v-bind="$attrs"
  >
    <!-- Generic back button -->
    <template v-if="showMobileBackButton">
      <re-button
        icon="mdi-arrow-left"
        size="sm"
        class="q-mx-sm"
        @click="redirectToPrevRoute"
      />

      <p
        v-if="mobileTitle"
        class="text-black text-bold q-my-auto"
      >
        {{ mobileTitle }}
      </p>
    </template>
    <!-- Back button specific to valuer tool -->
    <template v-if="!mobileDrawerOpen && !showMobileBackButton">
      <re-button
        v-if="showValuerToolBackButton"
        class="lt-md text-black"
        icon="mdi-chevron-left"
        flat
        size="md"
        @click="handleMobileBack"
      />

      <p
        v-if="mobileTitle"
        class="text-black text-bold q-my-auto"
      >
        {{ mobileTitle }}
      </p>
    </template>

    <template v-if="mobileDrawerOpen || !mobileTitle">
      <q-btn
        :to="{
          name: email ? 'base' : 'login',
          query: { resetCurRequest: true },
        }"
        flat
        padding="0 .5em"
      >
        <img
          :src="logo"
          class="logo"
          width="148"
        >
      </q-btn>
    </template>

    <div
      v-if="$q.screen.lt.md"
      class="row items-center no-wrap q-ml-auto"
    >
      <re-button
        v-if="authenticated && talkjs.enabled"
        tag="router-link"
        size="sm"
        icon="mdi-face-agent"
        class="inbox-button q-mx-md"
        :to="{ name: 'inbox', query: { resetCurRequest: true } }"
        :class="{ 'inbox-button--unread': talkjs.totalUnreads }"
      />

      <re-button
        v-if="props.hasSideMenu"
        :class="{ 'bg-blue-grey-2': menuOpen }"
        size="sm"
        icon="mdi-menu"
        @click="menuOpen = !menuOpen"
      />
    </div>

    <template v-if="$q.screen.gt.sm">
      <GoToDvmInput class="q-ml-sm" />

      <div class="q-ml-auto row no-wrap">
        <NewRequestButton
          v-if="authenticated && newRequestRelevant"
          class="col-xs-12 col-sm-auto q-ml-md"
          :label="newRequestButtonLabel"
          size="sm"
          :collapse-to-project="true"
        >
          <q-tooltip
            v-if="isCompact"
            class="bg-accent"
          >
            {{ t('dashboard.new_request.title') }}
          </q-tooltip>
        </NewRequestButton>

        <re-button
          v-if="authenticated && talkjs.enabled"
          tag="router-link"
          size="sm"
          icon="mdi-face-agent"
          class="inbox-button q-ml-md"
          :to="{ name: 'inbox', query: { resetCurRequest: true } }"
          :class="{ 'inbox-button--unread': talkjs.totalUnreads }"
        />

        <re-button
          v-if="!isLoginPage"
          icon="mdi-account"
          size="sm"
          class="q-ml-md"
        >
          <template #overlay>
            <q-popup-proxy>
              <NavbarItems />
            </q-popup-proxy>
          </template>
        </re-button>
      </div>
    </template>

    <template v-if="isLoginPage">
      <LangSelector size="xs" />
    </template>
  </q-toolbar>

  <router-view
    v-if="$q.screen.lt.md"
    v-slot="{ Component }"
    name="navigation"
  >
    <component
      :is="Component"
      :navbar-mode="true"
    />
  </router-view>
</template>

<script setup lang="ts">
import { useQuasar } from 'quasar'
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { useConfig } from '@/composables/useConfig'
import { useCurUser } from '@/composables/useCurUser'
import { useValuerSidebarDrawer } from '@/composables/useValuerSidebarDrawer'
import { talkjsKey } from '@/keys'
import { useRenderedSections } from '@/layout/composables/useRenderedSections'
import enums from '@/plugins/enums'

import GoToDvmInput from './GoToDvmInput.vue'
import LangSelector from './LangSelector.vue'
import NavbarItems from './NavbarItems.vue'
import NewRequestButton from './NewRequestButton.vue'

const props = defineProps({
  hasSideMenu: {
    type: Boolean,
    default: false,
  },
})
const menuOpen = defineModel('menuOpen', { type: Boolean, required: true })
const { t, te } = useI18n()
const $q = useQuasar()

const talkjs = inject(talkjsKey)!
const { mobileDrawerOpen } = useValuerSidebarDrawer()
const { sections: renderedSections } = useRenderedSections()

const route = useRoute()
const router = useRouter()
const config = useConfig()
const { email, accessibleModules, authenticated } = useCurUser()

const newRequestRelevant = computed(() => {
  return (
    !accessibleModules.value.includes(enums.modules.CAT)
    || !['dashboard', 'catalog-start'].includes(route.name as string)
  )
})
const isCompact = computed(() => $q.screen.sm)

const newRequestButtonLabel = computed(() => {
  const isOnlyCatalogUser
    = accessibleModules.value.length === 1 && accessibleModules.value.includes(enums.modules.CAT)
  const label = isOnlyCatalogUser ? t('catalog.sidebar.new_button') : t('dashboard.new_request.title')
  return isCompact.value ? '' : label
})

const mobileTitle = computed(() => {
  if ($q.screen.gt.sm)
    return false

  if (route.name === 'valuation.valuer-app') {
    const activeHash = route.hash.replace(/^#/, '')
    const activeSection = renderedSections.value.find(section => section.hash === activeHash)
    if (activeSection)
      return activeSection.label
  }

  const i18nParams: Record<string, unknown> = {}
  const title = route.meta?.mobile?.title
  if (title) {
    i18nParams[title] = route.query[title]
    return te(title) ? t(title, { ...i18nParams }) : title
  }
  return false
})

const showMobileBackButton = computed(() => {
  return $q.screen.lt.md && route.meta?.mobile?.showBackButton
})

const showValuerToolBackButton = computed(() => {
  // Show back button on mobile for valuer-app routes (opens drawer) or routes with prevRoute
  return $q.screen.lt.md && route.meta?.mobile?.showValuerSidebar
})

function handleMobileBack() {
  // If we have a drawer injected (valuer-app routes), open it
  if (route.meta?.mobile?.showValuerSidebar) {
    mobileDrawerOpen.value = !mobileDrawerOpen.value
  }
}

const isLoginPage = computed(() => route.name === 'login')

const logo = computed(() => {
  switch (config.THEME_ID) {
    case 'ing':
      return new URL('@/assets/img/themes/rockestate-ing.svg', import.meta.url).href
    default:
      return new URL('@/assets/icons/rockestate-logo-horizontal-dark.svg', import.meta.url).href
  }
})

function redirectToPrevRoute() {
  const prevRoute = route.meta?.prevRoute
  if (prevRoute) {
    router.push(prevRoute)
  }
  else {
    router.back()
  }
}
</script>

<style lang="scss" scoped>
.inbox-button {
  position: relative;

  &--unread {
    &::after {
      content: '';
      position: absolute;
      top: 10px;
      right: 10px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: var(--q-warning);
    }
  }
}
#navbar {
  // padding: 0 15px;
  border-bottom: 1px solid $border;
  @media print {
    display: none;
  }
}
</style>
